import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {actionButtonSx} from 'src/components/common/actionButton.sx';

/** The same card every table sits in, so the page reads as one surface rather than two. */
const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

export const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    // Clears the app bar, the way the list screens do now that their page header is gone.
    mt: 2.5,
};

export const identityCardSx: SxProps<Theme> = cardSx;

/**
 * The card's own header.
 *
 * A faint wash rather than flat white: it carries the id, the status and the three actions, and the
 * tint is what separates it from the four facts below without a second border.
 */
export const identityHeaderSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    px: 3,
    // Taller than the section header on the table below it: this card is the page's subject, and the
    // difference in height says so before anything is read. Same proportion as the tariff page.
    py: 5,
    background: `linear-gradient(180deg, ${theme.palette.primaryColor.A50} 0%, ${theme.palette.common.white} 100%)`,
};

export const tagBadgeSx: SxProps<Theme> = {
    width: '58px',
    height: '58px',
    borderRadius: '14px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '30px'},
};

export const tagTitleSx: SxProps<Theme> = {
    fontSize: '1.625rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
    fontVariantNumeric: 'tabular-nums',
};

export const tagSubtitleSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.25,
};

/** Active or blocked, as a pill with a dot — the same shape the list uses for a state. */
export const statusPillSx = (blocked: boolean): SxProps<Theme> => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    fontSize: '0.6875rem',
    fontWeight: 600,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    background: blocked ? '#FDECF1' : '#EAF6EC',
    color: blocked ? theme.palette.brand.dark : theme.palette.success.dark,
    border: `1px solid ${blocked ? theme.palette.brand.light : theme.palette.success.light}`,
    '&::before': {
        content: '""',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: blocked ? theme.palette.brand.main : theme.palette.success.main,
    },
});

export const identityFactsSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'},
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

export const factSx: SxProps<Theme> = {
    px: 3,
    py: 3.5,
    borderRight: `1px solid ${theme.palette.neutral[100]}`,
    '&:last-of-type': {borderRight: 'none'},
};

export const factLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const factValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    mt: 0.4,
};

export const ownerLinkSx: SxProps<Theme> = {
    ...factValueSx as object,
    color: theme.palette.primary.dark,
    textDecoration: 'none',
    '&:hover': {textDecoration: 'underline'},
};

/** The owner's kind, as a quiet chip — it is a classification, not a state, so it is not the pill. */
export const ownerTypeChipSx: SxProps<Theme> = {
    display: 'inline-flex',
    mt: 0.6,
    fontSize: '0.6875rem',
    fontWeight: 600,
    px: 1.1,
    py: 0.4,
    borderRadius: '999px',
    background: theme.palette.primaryColor.A50,
    color: theme.palette.primary.dark,
    border: `1px solid ${theme.palette.primaryColor.A100}`,
};

export const editButtonSx: SxProps<Theme> = actionButtonSx('32px', 'secondary');
export const blockButtonSx: SxProps<Theme> = actionButtonSx('32px', 'secondary');
export const deleteButtonSx: SxProps<Theme> = actionButtonSx('32px', 'danger');
